import { useEffect, useState } from "react";
import axios from "axios";
import Header from "../../../components/Header";
import { motion } from "framer-motion";
import { MapPin, CalendarDays } from "lucide-react";

function MyReservations() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        const res = await axios.get(
          "https://api.komorotel.com/api/reservation/user",
          { withCredentials: true }
        );
        setReservations(res.data);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };

    fetchReservations();
  }, []);

  const handleCancelRequest = async (id) => {
    const confirm = window.confirm(
      "Es-tu sûr de vouloir annuler cette réservation ?"
    );
    if (!confirm) return;

    try {
      await axios.post(
        `https://api.komorotel.com/api/reservation/${id}/cancellation-request`,
        { reservationId: id },
        { withCredentials: true }
      );

      setReservations((prev) =>
        prev.map((r) =>
          r._id === id ? { ...r, status: "pending_cancel" } : r
        )
      );
    } catch (err) {
      alert(err.response?.data?.message || "Erreur");
    }
  };

  const getStatusUI = (status) => {
    switch (status) {
      case "confirmed":
        return { text: "Confirmée", color: "bg-green-500" };
      case "pending_cancel":
        return { text: "Annulation en cours", color: "bg-yellow-500" };
      case "cancelled":
        return { text: "Annulée", color: "bg-red-500" };
      default:
        return { text: "Inconnue", color: "bg-gray-400" };
    }
  };

  return (
    <>
      <Header />

      <div className="pt-32 pb-20 bg-gray-50 min-h-screen">
        <div className="max-w-5xl mx-auto px-4">

          <div className="mb-10">
            <h1 className="text-3xl font-semibold text-gray-900">
              Mes réservations
            </h1>
            <p className="text-gray-500 mt-1">
              Consultez et gérez vos séjours
            </p>
          </div>

          {loading && <p className="text-gray-500">Chargement...</p>}

          {!loading && reservations.length === 0 && (
            <div className="bg-white rounded-2xl p-8 text-center border">
              <p className="text-gray-500">
                Aucune réservation pour le moment.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-6">

            {reservations.map((r, i) => {

              const status = getStatusUI(r.status);

              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const checkIn = new Date(r.checkInDate);
              const checkOut = new Date(r.checkOutDate);

              checkIn.setHours(0, 0, 0, 0);
              checkOut.setHours(0, 0, 0, 0);

              // 🔥 STATE séjour
              const isUpcoming = today < checkIn;
              const isOngoing = today >= checkIn && today < checkOut;
              const isPast = today >= checkOut;


              // 🔥 POLICY (SAFE)
                const hasPolicy = !!r.ratePlanId?.cancellationPolicy;

                const policy = r.ratePlanId?.cancellationPolicy;

                const now = new Date();

                const diffHours =
                  (checkIn.getTime() - now.getTime()) /
                  (1000 * 60 * 60);

                let refundText = "";
                let policyText = "";
                let policyColor = "text-gray-500";
                let isRefundable = true;

                // 🔥 CAS SANS POLICY (IMPORTANT)
                if (!hasPolicy) {
                  policyText = "Conditions d’annulation à confirmer avec l’hôtel";
                  refundText = "Remboursement selon validation de l’hôtel";
                  policyColor = "text-gray-500";
                  isRefundable = true; // ✅ on autorise
                }

                // 🔥 FREE
                if (hasPolicy && policy.type === "free") {
                  const isFree = diffHours >= policy.hoursBeforeArrival;

                  policyText = `Annulation gratuite jusqu’à ${policy.hoursBeforeArrival}h avant l’arrivée`;

                  refundText = isFree
                    ? "Remboursement total si annulation maintenant"
                    : "Non remboursable à cette date";

                  policyColor = isFree ? "text-green-600" : "text-red-500";
                }

                // 🔥 PARTIAL
                if (hasPolicy && policy.type === "partial") {
                  const isFree = diffHours >= policy.hoursBeforeArrival;

                  policyText = `Gratuit jusqu’à ${policy.hoursBeforeArrival}h avant l’arrivée puis ${policy.penaltyPercentage}% de frais`;

                  refundText = isFree
                    ? "Remboursement total si annulation maintenant"
                    : `Remboursement ${100 - policy.penaltyPercentage}%`;

                  policyColor = "text-yellow-600";
                }

                // 🔥 NON REFUNDABLE
                if (hasPolicy && policy.type === "non_refundable") {
                  policyText = "Non remboursable";
                  refundText = "Aucun remboursement";
                  policyColor = "text-red-500";
                  isRefundable = false;
                }

              const canCancel =
                r.status === "confirmed" &&
                isUpcoming &&
                isRefundable;

              return (
                <motion.div
                  key={r._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="group bg-white rounded-2xl border overflow-hidden hover:shadow-md transition"
                >

                  <div className="flex flex-col md:flex-row">

                    {/* IMAGE */}
                    <div className="relative md:w-[240px] h-[180px] overflow-hidden">
                      <img
                        src={r.roomId?.photos?.[0]}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>

                    {/* CONTENT */}
                    <div className="flex-1 p-5 flex flex-col justify-between">

                      <div>

                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin size={14} />
                          {r.hotelId?.name} · {r.hotelId?.city}
                        </p>

                        <h2 className="text-lg font-semibold text-gray-900 mt-1">
                          {r.roomId?.title}
                        </h2>

                        <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
                          <CalendarDays size={14} />
                          {new Date(r.checkInDate).toLocaleDateString()} →{" "}
                          {new Date(r.checkOutDate).toLocaleDateString()}
                        </p>

                        <p className="text-xs text-gray-400 mt-2">
                          Réf : {r.reservationId}
                        </p>

                        {/* 🔥 BADGE séjour */}
                        <div className="mt-2">
                          {isUpcoming && (
                            <span className="text-xs text-blue-500">
                              À venir
                            </span>
                          )}
                          {isOngoing && (
                            <span className="text-xs text-green-600">
                              En cours
                            </span>
                          )}
                          {isPast && (
                            <span className="text-xs text-gray-400">
                              Terminé
                            </span>
                          )}
                        </div>

                        {/* 🔥 POLICY */}
                        <div className="mt-2">
                          <p className={`text-xs ${policyColor}`}>
                            {policyText}
                          </p>
                        </div>

                        <p className="text-xs text-gray-500 mt-1">
                          {refundText}
                        </p>

                      </div>

                      {/* BOTTOM */}
                      <div className="flex items-center justify-between mt-5">

                        <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                          <span className={`w-2 h-2 rounded-full ${status.color}`}></span>
                          {status.text}
                        </span>

                        <div className="text-right">

                          <p className="text-lg font-semibold text-gray-900">
                            {r.totalPrice}€
                          </p>

                          {canCancel && (
                            <button
                              onClick={() => handleCancelRequest(r._id)}
                              className="text-sm mt-1 text-red-500 hover:text-red-600"
                            >
                              Demander l’annulation
                            </button>
                          )}

                          {!canCancel && r.status === "confirmed" && (
                            <p className="text-xs text-gray-400">
                              Annulation non disponible
                            </p>
                          )}

                          {r.status === "pending_cancel" && (
                            <p className="text-xs text-yellow-500">
                              ⏳ En attente de validation
                            </p>
                          )}

                        </div>

                      </div>

                    </div>

                  </div>

                </motion.div>
              );
            })}

          </div>
        </div>
      </div>
    </>
  );
}

export default MyReservations;